/**
 * Thin client for existing /api/guest-ai/* — no parallel guest system.
 */

const STORAGE_KEY = 'linas_guest_session_id';
const DEVICE_KEY = 'linas_guest_device_id';

/**
 * @typedef {Error & { status?: number; body?: unknown; code?: string }} GuestApiError
 */

const UUID_V4 = /^guest-[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const G_ID = /^g_[0-9a-f]{32}$/;

/**
 * History is keyed by this id. Guessable or constant ids are one shared transcript.
 * @param {string} sessionId
 * @returns {boolean}
 */
export function isUnguessableGuestSessionId(sessionId) {
  const lowered = String(sessionId || '').trim().toLowerCase();
  if (UUID_V4.test(lowered)) return true;
  if (!G_ID.test(lowered)) return false;
  return new Set(lowered.slice(2)).size >= 8;
}

function randomId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `guest-${crypto.randomUUID()}`;
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  }
  if (!bytes.some((b) => b !== 0)) {
    const salt = Date.now();
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = (Math.floor(Math.random() * 256) ^ ((salt >>> (i % 4)) & 0xff) ^ (i * 17)) & 0xff;
    }
    if (!bytes.some((b) => b !== 0)) bytes[0] = 1;
  }
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `g_${hex}`;
}

export function getOrCreateGuestDeviceId() {
  try {
    const existing = localStorage.getItem(DEVICE_KEY);
    if (existing && existing.length >= 8 && existing.length <= 80) return existing;
    const next = randomId();
    localStorage.setItem(DEVICE_KEY, next);
    return next;
  } catch {
    return randomId();
  }
}

function guestHeaders() {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Guest-Device-Id': getOrCreateGuestDeviceId(),
  };
}

/** Drop the stored id and start a guest thread that has no previous messages. */
export function rotateGuestSessionId() {
  const next = randomId();
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* In-memory id still opens this chat when storage is blocked. */
  }
  return next;
}

export function getOrCreateGuestSessionId() {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing && isUnguessableGuestSessionId(existing)) return existing;
    if (existing) localStorage.removeItem(STORAGE_KEY);
  } catch {
    return randomId();
  }
  return rotateGuestSessionId();
}

/**
 * @param {Response} response
 */
async function parseJson(response) {
  const text = await response.text();
  if (!text) return {};
  return JSON.parse(text);
}

/**
 * @param {string} message
 * @param {number} [status]
 * @param {unknown} [body]
 * @param {string} [code]
 * @returns {GuestApiError}
 */
function guestApiError(message, status, body, code) {
  /** @type {GuestApiError} */
  const err = new Error(message);
  if (status != null) err.status = status;
  if (body !== undefined) err.body = body;
  if (code) err.code = code;
  return err;
}

/**
 * @param {unknown} body
 */
function detailCode(body) {
  if (!body || typeof body !== 'object') return null;
  const detail = /** @type {{ detail?: unknown }} */ (body).detail;
  if (!detail || typeof detail !== 'object') return null;
  const row = /** @type {{ code?: unknown; error?: unknown }} */ (detail);
  if (typeof row.code === 'string') return row.code;
  if (typeof row.error === 'string') return row.error;
  return null;
}

/**
 * @param {string} guestSessionId
 * @param {string} [language]
 */
export async function ensureGuestSession(guestSessionId, language = 'en') {
  const response = await fetch('/api/guest-ai/session', {
    method: 'POST',
    headers: guestHeaders(),
    body: JSON.stringify({ guest_session_id: guestSessionId, language }),
  });
  const body = await parseJson(response);
  if (!response.ok) {
    throw guestApiError('Guest session failed', response.status, body);
  }
  return body.session;
}

/**
 * @param {Response} response
 * @param {(text: string) => void} [onDelta]
 */
async function readGuestSse(response, onDelta) {
  const reader = response.body && typeof response.body.getReader === 'function' ? response.body.getReader() : null;
  const decoder = new TextDecoder();
  let carry = '';
  /** @type {{ body: Record<string, unknown> | null }} */
  const found = { body: null };
  /**
   * @param {string} chunk
   */
  const take = (chunk) => {
    carry += chunk;
    const parts = carry.split('\n\n');
    carry = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find((row) => row.startsWith('data:'));
      if (!line) continue;
      const payload = /** @type {Record<string, unknown>} */ (JSON.parse(line.slice(5).trim()));
      const kind = String(payload.type || '');
      if (kind === 'delta' && payload.text && typeof onDelta === 'function') {
        onDelta(String(payload.text));
      }
      if (kind === 'error') {
        throw guestApiError(String(payload.message || payload.text || 'Guest message failed'), 503, payload);
      }
      if (kind === 'done') found.body = payload;
    }
  };
  if (reader) {
    for (;;) {
      const next = await reader.read();
      if (next.done) break;
      take(typeof next.value === 'string' ? next.value : decoder.decode(next.value, { stream: true }));
    }
    if (carry.trim()) {
      const rest = carry;
      carry = '';
      take(`${rest}\n\n`);
    }
  } else {
    take(`${await response.text()}\n\n`);
  }
  const doneBody = found.body;
  if (!doneBody) {
    throw guestApiError('Guest message failed', response.status, doneBody);
  }
  if (doneBody.success === false && doneBody.code === 'GUEST_QUESTION_LIMIT') {
    return { ok: false, session: doneBody.session, gateMessages: doneBody.message };
  }
  if (doneBody.success !== true) {
    throw guestApiError('Guest message failed', response.status, doneBody);
  }
  return { ok: true, session: doneBody.session, message: doneBody.message };
}

/**
 * @param {string} guestSessionId
 * @param {string} content
 * @param {string} [language]
 * @param {(text: string) => void} [onDelta]
 */
export async function sendGuestMessage(guestSessionId, content, language = 'en', onDelta) {
  const headers = guestHeaders();
  headers.Accept = 'text/event-stream, application/json';
  const response = await fetch('/api/guest-ai/session/messages', {
    method: 'POST',
    headers,
    body: JSON.stringify({ guest_session_id: guestSessionId, content, language }),
  });
  if ((response.headers.get('content-type') || '').includes('text/event-stream')) {
    return readGuestSse(response, onDelta);
  }
  const body = await parseJson(response);
  if (response.status === 400) {
    const code = detailCode(body) || 'rejected';
    throw guestApiError(code, 400, body, code);
  }
  if (!response.ok) {
    throw guestApiError('Guest message failed', response.status, body);
  }
  if (body?.success === false && body?.code === 'GUEST_QUESTION_LIMIT') {
    return { ok: false, session: body.session, gateMessages: body.message };
  }
  return { ok: true, session: body.session, message: body.message };
}
